import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosRequest } from "../axiosConfig";

// Constants for document statuses
const POLLING_STATUSES = ["UPLOADING", "UPLOADED", "PROCESSING"];

const normalizeStatus = (status) => {
  return typeof status === "string" ? status.toUpperCase() : status;
};

const DocumentStatusMonitor = ({ showData, setShowData }) => {
  const queryClient = useQueryClient();

  // Get IDs of documents that need polling
  const documentsToWatch = useMemo(() => {
    const faxIds = [];
    const childPdfIds = [];

    showData.forEach((fax) => {
      // Check parent fax status
      if (POLLING_STATUSES.includes(normalizeStatus(fax.Status))) {
        faxIds.push(fax.Id);
      }

      // Check child PDFs status
      if (fax.ChildPdf && fax.ChildPdf.length > 0) {
        fax.ChildPdf.forEach((child) => {
          if (
            !child.deleted &&
            POLLING_STATUSES.includes(normalizeStatus(child.status))
          ) {
            childPdfIds.push(child.id);
          }
        });
      }
    });

    return { faxIds, childPdfIds };
  }, [showData]);

  // Determine if polling should be active
  const shouldPoll = useMemo(() => {
    return (
      documentsToWatch.faxIds.length > 0 ||
      documentsToWatch.childPdfIds.length > 0
    );
  }, [documentsToWatch]);

  // Status polling query
  const {
    data: statusData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["documentStatus", documentsToWatch],
    queryFn: async () => {
      // Skip API call if nothing to poll
      if (!shouldPoll) return null;

      // Build URL with parameters
      let url = "/documents/status?";

      // Add fax IDs
      documentsToWatch.faxIds.forEach((id) => {
        url += `faxIds=${encodeURIComponent(id)}&`;
      });

      // Add child PDF IDs
      documentsToWatch.childPdfIds.forEach((id) => {
        url += `childPdfIds=${encodeURIComponent(id)}&`;
      });

      // Add timestamp to prevent caching
      url += `_=${new Date().getTime()}`;

      // Make the request
      const response = await axiosRequest.get(url);
      return response.data;
    },
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds if needed
    enabled: shouldPoll, // Only run query if there are documents to poll
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: 3, // Retry failed requests 3 times
  });

  // Update documents when new status data is received
  // Make a minor adjustment to the useEffect in DocumentStatusMonitor
  useEffect(() => {
    if (statusData && (statusData.faxStatuses || statusData.childStatuses)) {
      setShowData((prevData) => {
        let hasChanges = false;
        const updatedData = prevData.map((fax) => {
          // Always create a new reference for each object for better rendering
          let updatedFax = { ...fax };
          let faxUpdated = false;

          // Update parent status if needed
          if (statusData.faxStatuses && statusData.faxStatuses[fax.Id]) {
            const newStatus = statusData.faxStatuses[fax.Id];
            if (normalizeStatus(fax.Status) !== normalizeStatus(newStatus)) {
              updatedFax.Status = newStatus;
              faxUpdated = true;
              hasChanges = true;
            }
          }

          // Update child PDFs if needed
          if (
            fax.ChildPdf &&
            fax.ChildPdf.length > 0 &&
            statusData.childStatuses
          ) {
            const updatedChildren = fax.ChildPdf.map((child) => {
              if (statusData.childStatuses[child.id]) {
                const newStatus = statusData.childStatuses[child.id];
                if (
                  normalizeStatus(child.status) !== normalizeStatus(newStatus)
                ) {
                  faxUpdated = true;
                  hasChanges = true;
                  return { ...child, status: newStatus };
                }
              }
              return child;
            });

            if (faxUpdated) {
              updatedFax.ChildPdf = updatedChildren;
            }
          }

          return faxUpdated ? updatedFax : fax;
        });

        return hasChanges ? updatedData : prevData;
      });

      // If polling conditions might have changed, invalidate the query
      queryClient.invalidateQueries({ queryKey: ["documentStatus"] });
    }
  }, [statusData, setShowData, queryClient]);

  // Debug display (optional - you can remove this in production)
  return (
    <div className="polling-status" style={{ display: "none" }}>
      <div>Polling: {shouldPoll ? "Active" : "Inactive"}</div>
      <div>
        Documents watching:{" "}
        {documentsToWatch.faxIds.length + documentsToWatch.childPdfIds.length}
      </div>
      {isError && <div>Error: {error.message}</div>}
    </div>
  );
};

export default DocumentStatusMonitor;
